/**
 * Every voice connection open in this window (RFC 0307), so there is never
 * more than one.
 *
 * Kept on `globalThis`, not in a module: a hot reload loads a fresh copy of
 * the modules that own a call, and the copy that held the old call is simply
 * dropped, its connection still open. Two connections on one session meant
 * two voices hearing every line and two answering it. Whatever owns a call,
 * and however many copies of that owner exist, a new connection hangs up
 * every other one first.
 */

const KEY = '__modLiveVoiceCalls'

function live(): Set<() => void> {
  const g = globalThis as unknown as Record<string, Set<() => void> | undefined>
  let set = g[KEY]
  if (!set) {
    set = new Set()
    g[KEY] = set
  }
  return set
}

/** Hang up every voice connection still open in this window. */
export function hangUpAllCalls(): void {
  for (const hangUp of [...live()]) {
    try {
      hangUp()
    } catch {
      // A connection already gone has nothing left to close.
    }
  }
  live().clear()
}

/**
 * Register a connection about to open. Ends every other one first, and
 * returns the call to make when this one ends on its own.
 */
export function claimTheLine(hangUp: () => void): () => void {
  hangUpAllCalls()
  live().add(hangUp)
  return () => {
    live().delete(hangUp)
  }
}
